import React, { useState } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  Input,
  Textarea,
  Spinner,
  Toast,
  ToastTitle,
  ToastBody,
  useToastController,
} from '@fluentui/react-components';

import { Providers, ProviderState } from '@microsoft/mgt-element';

import { useAppStore } from '@/stores/chatbotStore';
import { Chatbot, ChatbotCreate } from '@/types/api';
import apiClient from '@/services/api';

const CreateChatbotDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (chatbot: ChatbotCreate) => void;
}> = ({ open, onOpenChange, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleCreate = () => {
    if (name.trim()) {
      onCreate({ name: name.trim(), description: description.trim() || undefined });
      setName('');
      setDescription('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogTitle>Creer un nouveau chatbot</DialogTitle>
        <DialogContent>
          <DialogBody>
            <div className="dialog-form">
              <div className="dialog-field">
                <label className="dialog-label">Nom du chatbot *</label>
                <Input
                  value={name}
                  onChange={(_, data) => setName(data.value)}
                  placeholder="Ex: Assistant Documentation"
                  autoFocus
                />
              </div>
              <div className="dialog-field">
                <label className="dialog-label">Description (optionnel)</label>
                <Textarea
                  value={description}
                  onChange={(_, data) => setDescription(data.value)}
                  placeholder="Decrivez brievement le role de ce chatbot..."
                  rows={2}
                />
              </div>
            </div>
          </DialogBody>
        </DialogContent>
        <DialogActions>
          <DialogTrigger>
            <Button appearance="secondary">Annuler</Button>
          </DialogTrigger>
          <Button appearance="primary" onClick={handleCreate} disabled={!name.trim()}>
            Creer
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

interface ChatbotManagerProps {
  onSelectChatbot?: (chatbot: Chatbot) => void;
}

const ChatbotManager: React.FC<ChatbotManagerProps> = ({ onSelectChatbot }) => {
  const {
    chatbots,
    currentChatbot,
    isLoadingChatbots,
    isLoading,
    createChatbot,
    deleteChatbot,
    selectChatbot,
    loadChatbots,
    loadDocuments,
  } = useAppStore();

  const { dispatchToast } = useToastController('global-toaster');

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [syncingChatbotId, setSyncingChatbotId] = useState<string | null>(null);

  const handleCreate = async (chatbot: ChatbotCreate) => {
    try {
      const created = await createChatbot(chatbot);
      onSelectChatbot?.(created);
    } catch (error) {
      console.error('Failed to create chatbot:', error);
    }
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteChatbot(deleteTarget);
    } catch (error) {
      console.error('Failed to delete chatbot:', error);
    }
    setDeleteTarget(null);
  };

  const handleSelect = (chatbot: Chatbot) => {
    selectChatbot(chatbot);
    onSelectChatbot?.(chatbot);
  };

  const handleSync = async (chatbot: Chatbot, e: React.MouseEvent) => {
    e.stopPropagation();
    if (syncingChatbotId) return;
    if (!Providers.globalProvider || Providers.globalProvider.state !== ProviderState.SignedIn) {
      dispatchToast(
        <Toast><ToastTitle>SharePoint non connecté</ToastTitle><ToastBody>Connectez-vous à SharePoint pour synchroniser.</ToastBody></Toast>,
        { intent: 'warning', timeout: 5000 }
      );
      return;
    }
    setSyncingChatbotId(chatbot.id);
    try {
      const token = await Providers.globalProvider.getAccessToken({
        scopes: ['Files.Read.All', 'Sites.Read.All'],
      });
      if (!token) {
        dispatchToast(
          <Toast><ToastTitle>Erreur d'authentification</ToastTitle><ToastBody>Impossible d'obtenir le token SharePoint.</ToastBody></Toast>,
          { intent: 'error', timeout: 5000 }
        );
        return;
      }
      const result = await apiClient.syncChatbot(chatbot.id, token);
      if (result.errors?.length > 0) {
        dispatchToast(
          <Toast>
            <ToastTitle>Erreur de synchronisation</ToastTitle>
            <ToastBody>{result.errors.length} erreur(s) : {result.errors.slice(0, 2).join(', ')}</ToastBody>
          </Toast>,
          { intent: 'error', timeout: 6000 }
        );
      } else if (result.added?.length > 0) {
        dispatchToast(
          <Toast>
            <ToastTitle>Synchronisation réussie</ToastTitle>
            <ToastBody>
              {result.added.length} document(s) ajouté(s){result.skipped?.length > 0 ? `, ${result.skipped.length} ignoré(s)` : ''}.
            </ToastBody>
          </Toast>,
          { intent: 'success', timeout: 5000 }
        );
        loadDocuments(chatbot.id);
      } else {
        dispatchToast(
          <Toast>
            <ToastTitle>Documents à jour</ToastTitle>
            <ToastBody>Aucun nouveau document trouvé{result.skipped?.length > 0 ? ` (${result.skipped.length} déjà présent(s))` : ''}.</ToastBody>
          </Toast>,
          { intent: 'info', timeout: 4000 }
        );
      }
      await loadChatbots();
    } catch (error: any) {
      dispatchToast(
        <Toast>
          <ToastTitle>Erreur</ToastTitle>
          <ToastBody>{error?.detail || error?.error || 'Erreur inconnue lors de la synchronisation.'}</ToastBody>
        </Toast>,
        { intent: 'error', timeout: 5000 }
      );
    } finally {
      setSyncingChatbotId(null);
    }
  };

  if (isLoadingChatbots) {
    return (
      <div className="chatbot-selector-loading">
        <Spinner size="tiny" />
      </div>
    );
  }

  return (
    <div className="chatbot-selector-content">
      <div className="chatbot-selector-list">
        {chatbots.map((chatbot) => (
          <div
            key={chatbot.id}
            className={`chatbot-selector-item ${currentChatbot?.id === chatbot.id ? 'active' : ''}`}
            onClick={() => handleSelect(chatbot)}
          >
            <div className="chatbot-selector-item-info">
              <span className="chatbot-selector-item-name">{chatbot.name}</span>
              <span className="chatbot-selector-item-meta">
                {chatbot.document_count} docs
              </span>
            </div>
            <div className="chatbot-selector-item-actions">
              {chatbot.is_default && (
                <button
                  className="chatbot-action-btn"
                  onClick={(e) => handleSync(chatbot, e)}
                  disabled={syncingChatbotId === chatbot.id}
                  title="Synchroniser SharePoint"
                >
                  {syncingChatbotId === chatbot.id ? '...' : '↻'}
                </button>
              )}
              <button
                className="chatbot-action-btn chatbot-action-delete"
                onClick={(e) => handleDelete(chatbot.id, e)}
                title="Supprimer"
              >
                ×
              </button>
            </div>
          </div>
        ))}

        {chatbots.length === 0 && (
          <div className="chatbot-selector-empty">
            Aucun chatbot
          </div>
        )}
      </div>

      <button
        className="chatbot-selector-create-btn"
        onClick={() => setIsCreateDialogOpen(true)}
        disabled={isLoading}
      >
        + Nouveau chatbot
      </button>

      <CreateChatbotDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onCreate={handleCreate}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(_, data) => { if (!data.open) setDeleteTarget(null); }}>
        <DialogSurface>
          <DialogTitle>Supprimer le chatbot</DialogTitle>
          <DialogContent>
            <DialogBody>
              Supprimer ce chatbot et toutes ses données ? Cette action est irréversible.
            </DialogBody>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button appearance="primary" style={{ background: '#d13438' }} onClick={confirmDelete}>Supprimer</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ChatbotManager;
